import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { GraduationCap, Clock, CalendarDays, Banknote } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ComingSoon from "@/components/ComingSoon";
import { format } from "date-fns";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";
import BookingConfirmation from "@/components/BookingConfirmation";
import PlayerAvatar from "@/components/PlayerAvatar";

export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Coaching() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingCoach, setBookingCoach] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/coaches"), api.get("/club")]).then(([c, k]) => { setCoaches(c.data); setClub(k.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (user?.role !== "admin") return <ComingSoon icon={GraduationCap} title="coming.coaching" text="coming.coaching_text" testId="coaching-coming-soon" />;
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="coaching-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center"><GraduationCap size={24} className="text-teal" /></div>
          <div>
            <h1 className="font-heading font-black text-3xl tracking-tight">Coaching</h1>
            <p className="text-muted-foreground text-sm">Private 60-minute lessons on the D&D Padel court</p>
          </div>
        </div>

        {coaches.length === 0 && (
          <div className="text-center py-20 rounded-3xl border border-dashed border-border/50" data-testid="no-coaches">
            <GraduationCap size={48} className="mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground text-lg mb-1">Coaching schedule coming soon</p>
            <p className="text-muted-foreground text-sm">Our coaches and lesson times will appear here.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {coaches.map((c, i) => (
            <motion.div key={c._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Card className="border-border/50 h-full" data-testid="coach-card">
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start gap-4 mb-4">
                    <PlayerAvatar name={c.name} avatarUrl={c.photo_url} size="lg" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-bold text-lg leading-tight">{c.name}</h3>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(c.specialties || []).map(s => <Badge key={s} variant="secondary" className="rounded-full text-[10px]">{s}</Badge>)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-black text-golden">Rs {Number(c.price_per_session).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">per lesson</p>
                    </div>
                  </div>
                  {c.bio && <p className="text-sm text-muted-foreground mb-4 flex-1">{c.bio}</p>}
                  <div className="text-xs text-muted-foreground space-y-1 mb-4">
                    <p className="flex items-center gap-1.5"><CalendarDays size={12} className="text-teal" /> {(c.available_days || []).map(d => DAY_NAMES[d]).join(", ") || "—"}</p>
                    <p className="flex items-center gap-1.5"><Clock size={12} className="text-teal" /> {(c.available_hours || "").replace("-", " – ")}</p>
                    <p className="flex items-center gap-1.5"><Banknote size={12} className="text-teal" /> Pay at the court or via MCB Juice</p>
                  </div>
                  <Button onClick={() => setBookingCoach(c)} className="w-full rounded-full bg-teal hover:bg-teal-600 text-white" data-testid={`book-coach-${c._id}`}>Book a lesson</Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {bookingCoach && <BookLessonDialog coach={bookingCoach} onClose={() => setBookingCoach(null)} onBooked={(s) => { setBookingCoach(null); setConfirmed(s); }} />}
      <BookingConfirmation open={!!confirmed} onClose={() => setConfirmed(null)} booking={confirmed} club={club} kind="coaching" />
    </div>
  );
}

function BookLessonDialog({ coach, onClose, onBooked }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [coachOff, setCoachOff] = useState(false);
  const [slot, setSlot] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/coaches/${coach._id}/availability?date=${format(date, "yyyy-MM-dd")}`)
      .then(r => { setSlots(r.data.slots || []); setCoachOff(!!r.data.coach_off); setSlot(null); }).catch(() => setSlots([]));
  }, [coach._id, date]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    setSaving(true);
    try {
      const res = await api.post("/coaching/sessions", { coach_id: coach._id, date: format(date, "yyyy-MM-dd"), start_time: slot.start, payment_method: paymentMethod, notes });
      onBooked(res.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="book-lesson-dialog">
        <DialogHeader><DialogTitle>Lesson with {coach.name}</DialogTitle><DialogDescription>60 minutes on Central Court · Rs {Number(coach.price_per_session).toLocaleString()}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Calendar mode="single" selected={date} onSelect={d => d && setDate(d)} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-xl border border-border/50 mx-auto" />
          <div>
            <p className="text-sm font-medium mb-2">{format(date, "EEE, MMM d")}</p>
            {coachOff ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="coach-off">{coach.name} doesn't coach on this day.</p> : (
              <div className="grid grid-cols-2 gap-2" data-testid="lesson-slots">
                {slots.map(s => (
                  <button key={s.start} disabled={!s.available} onClick={() => setSlot(s)} data-testid="lesson-slot"
                    className={`p-2 rounded-xl text-sm font-medium transition-all ${slot?.start === s.start ? "bg-teal text-white" : s.available ? "bg-muted/50 hover:bg-muted border border-border/50" : "bg-muted/20 text-muted-foreground/40 cursor-not-allowed line-through"}`}>
                    {s.start}
                  </button>
                ))}
                {slots.length === 0 && <p className="col-span-2 text-xs text-muted-foreground text-center py-4">No slots.</p>}
              </div>
            )}
          </div>
        </div>
        {slot && (
          <div className="space-y-3 pt-2">
            <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} />
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything the coach should know? (level, goals...)" className="rounded-xl min-h-[60px]" data-testid="lesson-notes" />
            <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-teal hover:bg-teal-600 text-white h-11" data-testid="lesson-confirm-btn">
              {saving ? "Booking..." : user ? `Book ${slot.start} · Rs ${Number(coach.price_per_session).toLocaleString()}` : "Sign in to book"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Free cancellation up to 24 hours before your lesson. No cancellations within 24 hours.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
